import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  bulkProcessingProgressAtom,
  isBulkProcessingAtom,
  multiSelectModeAtom,
  showServiceTemplatesSwapMenuAtom,
} from '@/atoms/_editorAtoms'
import {
  canvasRefAtom,
  insertMenuOpenAtom,
  isPageDeleting,
  openSwapImageMenuAtom,
  pageRefAtom,
  selectedPageAtom,
} from '@/atoms/editorAtoms'
import { BulkProcessingOverlay } from '@/components/editor/Canvas/BulkProcessingOverlay/BulkProcessingOverlay'
import * as S from '@/components/editor/Canvas/Canvas.styled'
import { EditorToolbar } from '@/components/editor/EditorToolbar/EditorToolbar'
import { SwapImageMenu } from '@/components/editor/InsertMenu/Menus/SwapImageMenu'
import { ServiceTemplatesSwapMenu } from '@/components/editor/ServiceTemplatesCustomizer/ServiceTemplatesSwapMenu.tsx'
import { Loading } from '@/components/global/Loading/Loading'
import { Page } from '@/components/page/Page'
import { EDITOR_CANVAS_PAGE_TOP_PX } from '@/components/page/Items/EditorTextBoxToolbar.geometry'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'

import { useBoundingBox } from '@/hooks/editor/useBoundingBox'
import { useZoom } from '@/hooks/editor/useZoom'

import { isEven } from '@/utils/global/isEven'
import { zLayers } from '@/utils/styling/zLayers'

export const Canvas = ({
  loadedPages,
  theme,
  themeOverride,
  setShowSlideout,
  baseLayout,
}) => {
  const { t } = useTranslation()
  const setCanvasRef = useSetRecoilState(canvasRefAtom)
  const setPageRef = useSetRecoilState(pageRefAtom)
  const selectedPage = useRecoilValue(selectedPageAtom)
  const canvasRef = useRef(null)
  const pageRef = useRef(null)
  const lastFitKey = useRef(null)
  const { fitToView } = useZoom()
  const isDeleting = useRecoilValue(isPageDeleting)
  const setInsertMenuOpen = useSetRecoilState(insertMenuOpenAtom)
  const [isSwapImageMenuOpen, setIsSwapImageMenuOpen] = useRecoilState(
    openSwapImageMenuAtom,
  )
  const [showServiceTemplatesSwapMenu, setShowServiceTemplatesSwapMenu] =
    useRecoilState(showServiceTemplatesSwapMenuAtom)
  const setMultiSelectMode = useSetRecoilState(multiSelectModeAtom)
  const isBulkProcessing = useRecoilValue(isBulkProcessingAtom)
  const bulkProcessingProgress = useRecoilValue(bulkProcessingProgressAtom)

  useBoundingBox(canvasRef, pageRef)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        setMultiSelectMode(true)
      }
    }

    const handleKeyUp = (e) => {
      if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
        setMultiSelectMode(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    const handleBlur = () => setMultiSelectMode(false)
    window.addEventListener('blur', handleBlur)

    return () => {
      setMultiSelectMode(false)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleBlur)
    }
  }, [setMultiSelectMode])

  useEffect(() => {
    if (pageRef?.current) {
      setPageRef(pageRef?.current)
    }

    // eslint-disable-next-line
  }, [pageRef.current])

  useEffect(() => {
    if (canvasRef?.current) {
      setCanvasRef(canvasRef?.current)
    }
    // eslint-disable-next-line
  }, [canvasRef.current])

  // Fit page to viewport on load and when page dimensions change
  useEffect(() => {
    const currentPage = loadedPages?.[selectedPage - 1]
    const product = currentPage?.product
    const productSize = product?.productSize
    const padding = product?.padding || {}
    const whiteBorder = product?.whiteBorder || 0

    if (!canvasRef?.current || !productSize?.width || !productSize?.height)
      return

    // Calculate full page dimensions including bleed/padding and border
    const fullWidth =
      productSize.width +
      (padding.left || 0) +
      (padding.right || 0) +
      whiteBorder * 2
    const fullHeight =
      productSize.height +
      (padding.top || 0) +
      (padding.bottom || 0) +
      whiteBorder * 2

    // Avoid redundant fits when dimensions haven't changed
    const fitKey = `${fullWidth}-${fullHeight}`
    if (lastFitKey.current === fitKey) return

    fitToView({
      pageWidth: fullWidth,
      pageHeight: fullHeight,
      canvasElement: canvasRef.current,
    })
    lastFitKey.current = fitKey
  }, [loadedPages, selectedPage, fitToView])

  return (
    <S.Canvas
      justify="center"
      height="100%"
      flex="1"
      data-editor-ui="editor-canvas"
      ref={canvasRef}
      align={isDeleting ? 'center' : undefined}
      onClick={() => setInsertMenuOpen(false)}
    >
      <Flex
        top="20px"
        full
        justify="center"
        position="absolute"
        data-bounding-box
        clickThrough
      >
        <Text zIndex={zLayers.interface}>
          {t('editor.canvas.pageLabel', {
            side: isEven(selectedPage)
              ? t('editor.canvas.backText')
              : t('editor.canvas.frontText'),
            currentPage: selectedPage,
            totalPages: loadedPages.length,
          })}
        </Text>
      </Flex>
      {loadedPages && (
        <Loading isLoading={isDeleting}>
          <Page
            key={loadedPages?.[selectedPage - 1]?.product?.pageId}
            position="relative"
            top={`${EDITOR_CANVAS_PAGE_TOP_PX}px`}
            theme={theme}
            themeOverride={themeOverride}
            page={loadedPages?.[selectedPage - 1]}
            pageRef={pageRef}
            isPMRegisterBook={false}
            isEven={isEven(selectedPage)}
            environment="editor"
            baseLayout={baseLayout}
          />
        </Loading>
      )}
      <EditorToolbar setShowSlideout={setShowSlideout} />
      <SwapImageMenu
        show={isSwapImageMenuOpen}
        setShow={setIsSwapImageMenuOpen}
        className="slide-out"
      />
      <ServiceTemplatesSwapMenu
        show={showServiceTemplatesSwapMenu}
        setShow={setShowServiceTemplatesSwapMenu}
      />
      {isBulkProcessing && (
        <BulkProcessingOverlay progress={bulkProcessingProgress} />
      )}
    </S.Canvas>
  )
}
